import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpSentEvent, HttpHeaderResponse, HttpProgressEvent, HttpResponse, HttpUserEvent, HttpErrorResponse, HttpEvent } from '@angular/common/http';
import { Observable, BehaviorSubject, throwError, of } from 'rxjs';
import { catchError, switchMap, finalize, filter, take, retry } from 'rxjs/operators';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { Auth, RegistryAuth } from 'src/app/models/auth';
import { GrantType } from '../../enums/grant-type.enum';
import { ResponseBase } from 'src/app/models/response-base';
import { LocalStoreKeys } from '../localstorage/local-store';
import { ApiError } from '../../enums/api-error.enum';
import { NewAlertService } from '../../components/new-alert/new-alert.service';
import { GatewayAuthService } from 'src/app/services/gateway-auth.service';
import { GatewayAuth } from 'src/app/models/response/gateway-auth-response';
import { SpinnerVisibilityService } from 'ng-http-loader';

@Injectable()
export class RequestInterceptor implements HttpInterceptor {
    isRefreshingToken = false;
    tokenSubject: BehaviorSubject<Auth> = new BehaviorSubject<Auth>(null);
    gatewaySubject: BehaviorSubject<GatewayAuth> = new BehaviorSubject<GatewayAuth>(null);

    constructor(
        private authenticationService: AuthenticationService,
        private gatewayAuthService: GatewayAuthService,
        private alertService: NewAlertService,
        private spinner: SpinnerVisibilityService
    ) {}

    // adiciona o token na requisição
    addToken(request: HttpRequest<any>, currentUserValue: Auth): HttpRequest<any> {
        return request.clone({
            setHeaders: {
                Authorization: `Bearer ${currentUserValue.token.accessToken}`,
            },
        });
    }

    // interceptar requisição
    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpSentEvent | HttpHeaderResponse | HttpProgressEvent | HttpResponse<any> | HttpUserEvent<any>> {

        // não armazenar cache
        request = request.clone({
            setHeaders: {
                'Cache-Control': 'no-cache',
                Pragma: 'no-cache',
                'Access-Control-Allow-Origin': '*',
            },
        });

        if (this.authenticationService.currentUserValue) {
            return next.handle(this.addToken(request, this.authenticationService.currentUserValue))
                .pipe(
                    retry(2),
                    catchError((error): Observable<any> => {
                        if (error.error === 'Invalid Token') {
                            const message = 'Você foi desconectado, por favor faça login novamente';
                            this.authenticationService.logout(message);
                            return;
                        }
                        if (error instanceof HttpErrorResponse) {
                            if (error.status === 401) {                                
                                return this.handle401Error(request, next);
                            } else if (error.status >= 500 && error.status < 600) {
                                return throwError('Erro de servidor, por favor tente novamente mais tarde.');
                            } else if (error.status === 400) {
                                return throwError(error.error?.message || 'Erro de requisição inválida.');
                            } else if (error.status === 404) {
                                return this.handle404Error(error);
                            } else {
                                return throwError('Ocorreu um erro, por favor tente novamente.');
                            }
                        } else {
                            return throwError(error);
                        }
                    })
                );
        }

        return next.handle(request);
    }

    // tratar 401 unauthorized
    handle401Error(request: HttpRequest<any>, next: HttpHandler) {
        if (!this.isRefreshingToken) {
            this.isRefreshingToken = true;
            this.tokenSubject.next(null);

            if (this.authenticationService.currentUserValue) {
                return this.authenticationService.refreshToken(this.authenticationService.currentUserValue.token.refreshToken)
                    .pipe(
                        switchMap((response: ResponseBase<Auth>) => {
                            if (response && response.result) {
                                if (response.status === ApiError.Sucesso) {
                                    this.authenticationService.setCurrentUserValue(response.result);
                                    this.tokenSubject.next(response.result);
                                    return next.handle(this.addToken(request, response.result));
                                } else {
                                    this.alertService.error(response.message);
                                    return this.logoutUser();
                                }
                            }
                            return this.logoutUser();
                        }),
                        catchError((error) => {
                            return this.logoutUser();
                        }),
                        finalize(() => {
                            this.isRefreshingToken = false;
                        })
                    );
            }
        } else {
            return this.tokenSubject.pipe(
                filter(token => token != null),
                take(1),
                switchMap(token => {
                    return next.handle(this.addToken(request, token));
                })
            );
        }
    }

    handle400Error(error): any {
        return throwError(error);
    }

    handle404Error(error): any {
        return throwError(error);
    }

    logoutUser() {
        this.authenticationService.logout();
        return throwError('error');
    }
}